import { Alert, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { casePath } from '@/api/runPath';
import type { FanOutState } from '@/api/types';

type Child = NonNullable<FanOutState['children']>[number];

const STATE_COLOR: Record<string, string | undefined> = {
  RUNNING: 'processing',
  SUCCEEDED: 'success',
  NOT_COUNTED: 'warning',
  FAILED: 'error',
  CANCELLED: undefined,
};

const ERROR_TEXT: Record<string, string> = {
  OVER_EVALUATION_FAILED: 'The list could not be read, so no child started.',
  QUORUM_ABOVE_ELEMENTS: 'The list is shorter than the quorum, so it could not be met.',
};

function limitText(l: NonNullable<FanOutState['limit']>): string {
  if (l.guardrail === 'budget') {
    return l.actual < 0
      ? 'The metering ledger could not be read, so the budget counts as reached.'
      : `Budget reached: ${l.actual} units spent of ${l.allowed}.`;
  }
  if (l.guardrail === 'maxFanOut') return `Too many items: ${l.actual}, and at most ${l.allowed} are allowed. No child started.`;
  return `Nested too deep: level ${l.actual}, and at most ${l.allowed} is allowed.`;
}

/** What counted toward the policy, and why the rest did not. Nothing to say before children exist. */
function countText(f: FanOutState): string | null {
  if (typeof f.required !== 'number' || !f.counts) return null;
  const parts = [`${f.counts.SUCCEEDED ?? 0} of ${f.required} needed have succeeded`];
  const notCounted = f.counts.NOT_COUNTED ?? 0;
  if (notCounted) {
    const rule = f.successOutcomes?.length ? `only ${f.successOutcomes.join(', ')} count` : 'REJECTED does not count';
    parts.push(`${notCounted} finished on an outcome that does not count (${rule})`);
  }
  if (f.counts.FAILED) parts.push(`${f.counts.FAILED} failed`);
  if (f.counts.CANCELLED) parts.push(`${f.counts.CANCELLED} cancelled`);
  if (f.counts.RUNNING) parts.push(`${f.counts.RUNNING} running`);
  return `${parts.join(', ')}.`;
}

/**
 * Children grouped under their fan-out, with cancelled ones shown as cancelled (OL-25). A child's link carries no subject:
 * it may have its own, and the child's page reads it from the run.
 */
export function FanOutPanel({ fanOuts }: { fanOuts?: Record<string, FanOutState> }) {
  const entries = Object.entries(fanOuts ?? {});
  if (entries.length === 0) return null;
  const columns: ColumnsType<Child> = [
    {
      title: 'Child run',
      key: 'run',
      render: (_, c) => (
        <Link className="ol-mono" style={{ fontSize: 12 }} to={casePath(c.runId)}>
          {c.key}
        </Link>
      ),
    },
    {
      title: 'State',
      key: 'state',
      width: 180,
      render: (_, c) => (
        <Tag bordered={false} color={STATE_COLOR[c.state]}>
          {c.state === 'NOT_COUNTED' ? 'FINISHED, NOT COUNTED' : c.state}
        </Tag>
      ),
    },
    { title: 'Outcome', key: 'outcome', width: 200, render: (_, c) => c.outcome ?? '—' },
  ];
  return (
    <div data-testid="fan-out-panel">
      {entries.map(([nodeId, f]) => (
        <section key={nodeId} aria-label={`Fan-out ${nodeId}`} style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>
            Fan-out: {nodeId}
            {typeof f.elements === 'number' ? ` (${f.elements} items)` : ''}
          </h3>
          {f.source === 'DECLARED' && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              data-testid="fan-out-declared"
              message="Directors as declared by the applicant in the room, not from a registry."
            />
          )}
          {countText(f) && (
            <p data-testid="fan-out-count" style={{ fontSize: 12.5, margin: '0 0 8px' }}>
              {countText(f)}
            </p>
          )}
          {f.limit && <Alert type="warning" showIcon style={{ marginBottom: 8 }} data-testid="fan-out-limit" message={limitText(f.limit)} />}
          {f.error && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={ERROR_TEXT[f.error] ?? f.error} />}
          {(f.children?.length ?? 0) > 0 && (
            <Table<Child> size="small" rowKey="key" pagination={false} columns={columns} dataSource={f.children} />
          )}
        </section>
      ))}
    </div>
  );
}
